import React from "react";
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Paper,
  Stack,
  Divider,
} from "@mui/material";
import {
  PersonAdd as PersonAddIcon,
  VpnKey as VpnKeyIcon,
  Send as SendIcon,
  PhoneIphone as PhoneIphoneIcon,
  AccountCircle as AccountCircleIcon,
  CheckCircle as CheckCircleIcon,
  Assignment as AssignmentIcon,
  Restaurant as RestaurantIcon,
  Message as MessageIcon,
} from "@mui/icons-material";

const steps = [
  {
    label: "Add Client",
    description: "Add a new client to your organization from the coach console.",
    Icon: PersonAddIcon,
  },
  {
    label: "Get Connect Code",
    description: "Each client receives a unique connect code that links them to your organization.",
    Icon: VpnKeyIcon,
  },
  {
    label: "Share Connect Code & App Link",
    description: "Send your client their connect code and a link to download the Mighty45 app from the App Store.",
    Icon: SendIcon,
  },
  {
    label: "Client Downloads App",
    description: "Your client downloads the Mighty45 mobile app from the App Store using the link you provided.",
    Icon: PhoneIphoneIcon,
  },
  {
    label: "Client Creates Account",
    description: "Your client creates their account in the mobile app and enters the connect code you shared.",
    Icon: AccountCircleIcon,
  },
  {
    label: "Client Connected",
    description: "Once connected, your client will see the Overview screen in the mobile app and is now part of your organization.",
    Icon: CheckCircleIcon,
  },
  {
    label: "Coach Assigns Plans",
    description: "You can now assign training plans, meal plans, and message your client directly through the coach console.",
    Icon: AssignmentIcon,
  },
];

const ClientOnboardingFlow: React.FC = () => {
  return (
    <Box sx={{ maxWidth: 800, mx: "auto", p: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: "bold", mb: 3 }}>
        How to Connect Your First Client
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Follow these simple steps to get your clients connected and start coaching them through the Mighty45 platform.
      </Typography>

      <Stepper orientation="vertical" sx={{ mt: 2 }}>
        {steps.map((step, index) => {
          const IconComponent = step.Icon;
          return (
            <Step key={step.label} active={true} completed={false}>
              <StepLabel
                StepIconComponent={() => (
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "50%",
                      backgroundColor: "primary.main",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: 2,
                    }}
                  >
                    <IconComponent />
                  </Box>
                )}
              >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {step.label}
              </Typography>
            </StepLabel>
            <StepContent>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mb: 3,
                  backgroundColor: "grey.50",
                  borderLeft: "4px solid",
                  borderColor: "primary.main",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {step.description}
                </Typography>
              </Paper>
            </StepContent>
          </Step>
          );
        })}
      </Stepper>

      <Divider sx={{ my: 4 }} />

      <Paper
        elevation={2}
        sx={{
          p: 3,
          backgroundColor: "primary.light",
          color: "white",
          borderRadius: 2,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <AssignmentIcon sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Once Connected
            </Typography>
          </Box>
        </Stack>
        <Stack spacing={1}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AssignmentIcon sx={{ fontSize: 20 }} />
            <Typography variant="body2">
              Assign custom training plans tailored to your client&apos;s goals
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <RestaurantIcon sx={{ fontSize: 20 }} />
            <Typography variant="body2">
              Create and share personalized meal plans
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <MessageIcon sx={{ fontSize: 20 }} />
            <Typography variant="body2">
              Message your client directly and track their progress
            </Typography>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};

export default ClientOnboardingFlow;

